import { Edit } from '@mui/icons-material';
import { Grid2, Typography } from '@mui/material';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Metadata } from '@thro-types/billing';

import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { useGetMetadataQuery, useMetadataMutation } from 'api/hooks/billing/metadata';
import { DataGrid, TypedGridColumns } from 'app/components';
import { Searchfield } from 'app/components/Searchfield';
import { DATEFORMATWITHTIME } from 'app/containers/Billing/utils';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { useTableQuery } from 'utils/hooks/useTableQuery';

import { MetadataDrawer } from './MetaDataDrawer';

export const MetadataTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [metadata, setMetadata] = useState<Metadata | undefined>(undefined);

  const {
    search,
    handleSearchTextChange,
    debouncedSearchText,
    page,
    pageSize,
    sortingModel,
    handlePaginationChange,
    handleSortModelChange
  } = useTableQuery({
    defaultSort: [{ field: 'key', sort: 'asc' }],
    defaultPageSize: 100,
    sortKey: 'metadata_sort',
    paginationKeys: { page: 'metadata_page', pageSize: 'metadata_pageSize' }
  });

  const { data, isFetching } = useGetMetadataQuery({
    params: {
      skip: page * pageSize,
      take: pageSize,
      sort: sortingModel[0]?.field,
      sortOrder: sortingModel[0]?.sort,
      search: debouncedSearchText
    }
  });

  const { update } = useMetadataMutation();

  const handleSaveClick = async value => {
    try {
      if (isSubmitting) {
        return;
      }

      await update.mutateAsync({
        key: value.key,
        data: value
      });
      setOpen(false);
      dispatch(
        AlertActions.success(t(translations.pages.billingSettings.mutation.metadataUpdateSuccess()))
      );
    } catch (error) {
      dispatch(
        AlertActions.error(t(translations.pages.billingSettings.mutation.metadataUpdateFailed()))
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const rowActions = useCallback(
    (params: GridRowParams<Metadata>) => {
      return [
        <GridActionsCellItem
          icon={
            <Edit
              style={{
                width: 'auto'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => {
            setMetadata(params.row);
            setOpen(true);
          }}
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<Metadata> => {
    return [
      { field: 'key', flex: 2, headerName: t(translations.pages.billingSettings.metadata.key()) },
      {
        field: 'value',
        flex: 2,
        headerName: t(translations.pages.billingSettings.metadata.text())
      },
      {
        field: 'updatedAt',
        flex: 1,
        headerName: t(translations.common.updatedAt()),
        renderCell: params => (
          <Typography variant="body2">
            {format(new Date(params.row.updatedAt), DATEFORMATWITHTIME, { locale: de })}
          </Typography>
        )
      },
      {
        field: 'actions',
        flex: 1,
        headerName: t(translations.common.actions()),
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [rowActions, t]);

  return (
    <Grid2 style={{ flexGrow: 1 }}>
      <MetadataDrawer
        open={open}
        setOpen={setOpen}
        metadata={metadata}
        handleSaveClick={handleSaveClick}
      />
      <Grid2 container justifyContent={'flex-start'} paddingBottom={1}>
        <Searchfield handleSearch={handleSearchTextChange} search={search} />
      </Grid2>
      <Grid2 container style={{ height: '80vh' }}>
        <DataGrid
          loading={isFetching}
          columns={columns}
          getRowHeight={() => 'auto'}
          //? THIS IS RECOMMENDED BY MUI IF AUTO IS USED
          sx={{
            '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': { py: '8px' },
            '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': { py: '15px' },
            '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': { py: '22px' }
          }}
          rows={data?.data || []}
          disableColumnResize
          disableColumnMenu
          disableRowSelectionOnClick
          paginationMode="server"
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={handlePaginationChange}
          sortingMode="server"
          sortModel={sortingModel}
          onSortModelChange={handleSortModelChange}
          rowCount={data?.count || 0}
        />
      </Grid2>
    </Grid2>
  );
};
